import {
  ContactEmail,
  OutreachDraftResponse,
  useLeadContactEmails,
  useLeadOutreachDraft,
} from "@inrange/building-manager-api-client";
import { useState } from "react";
import { Alert, Badge, Button, Spinner } from "react-bootstrap";

// Tenant + landlord contact emails for a lead that already has a site,
// auto-pulled by the backend from the configured sources (Gmail /
// HubSpot / Apollo). The operator can draft a short outreach email to the
// top contact for either role; the draft lands in their own Gmail.
const SOURCE_LABELS: Record<string, string> = {
  gmail: "Gmail",
  hubspot: "HubSpot",
  apollo: "Apollo",
};

const ContactRow: React.FC<{ contact: ContactEmail }> = ({ contact }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
    <div
      style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}
    >
      <a href={`mailto:${contact.email}`}>{contact.email}</a>
      {contact.sources.map((source) => (
        <Badge key={source} bg="light" text="dark" style={{ fontWeight: 400 }}>
          {SOURCE_LABELS[source] ?? source}
        </Badge>
      ))}
    </div>
    {(contact.name || contact.title) && (
      <div style={{ color: "#666", fontSize: 12 }}>
        {[contact.name, contact.title].filter(Boolean).join(" - ")}
      </div>
    )}
  </div>
);

const DraftResult: React.FC<{ result: OutreachDraftResponse }> = ({
  result,
}) => {
  if (result.blocked) {
    return (
      <Alert variant="warning" style={{ marginTop: 8, marginBottom: 0 }}>
        Not drafted: {result.reason}
      </Alert>
    );
  }
  return (
    <Alert variant="light" style={{ marginTop: 8, marginBottom: 0 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        {result.gmailDraftId
          ? "Draft created in your Gmail Drafts - review and send."
          : "Draft generated (Gmail placement unavailable - copy below)."}
      </div>
      {result.gmailError && (
        <div style={{ color: "#a60", fontSize: 12, marginBottom: 4 }}>
          {result.gmailError}
        </div>
      )}
      <pre
        style={{
          whiteSpace: "pre-wrap",
          fontSize: 12,
          margin: 0,
          fontFamily: "inherit",
        }}
      >
        {result.draftText}
      </pre>
    </Alert>
  );
};

const ContactColumn: React.FC<{
  title: string;
  role: "tenant" | "landlord";
  contacts: ContactEmail[];
  onDraft: (role: "tenant" | "landlord", contact: ContactEmail) => void;
  drafting: boolean;
  result: OutreachDraftResponse | undefined;
}> = ({ title, role, contacts, onDraft, drafting, result }) => {
  const top = contacts[0];
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 4,
        }}
      >
        <span style={{ fontWeight: 600 }}>{title}</span>
        <Button
          size="sm"
          variant="outline-primary"
          disabled={!top || drafting}
          title={
            top
              ? `Draft an outreach email to ${top.email} (lands in your Gmail Drafts)`
              : "No contact to draft to"
          }
          onClick={() => top && onDraft(role, top)}
        >
          {drafting ? "Drafting..." : "Draft email"}
        </Button>
      </div>
      {contacts.length === 0 ? (
        <div style={{ color: "#aaa" }}>No contacts found</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {contacts.map((contact) => (
            <ContactRow key={contact.email} contact={contact} />
          ))}
        </div>
      )}
      {result && <DraftResult result={result} />}
    </div>
  );
};

const LeadContactEmailsSection: React.FC<{ leadId: string }> = ({ leadId }) => {
  const { data, isLoading, isError } = useLeadContactEmails(leadId);
  const draftMutation = useLeadOutreachDraft(leadId);
  const [pendingRole, setPendingRole] = useState<
    "tenant" | "landlord" | null
  >(null);
  const [results, setResults] = useState<
    Partial<Record<"tenant" | "landlord", OutreachDraftResponse>>
  >({});

  const onDraft = (role: "tenant" | "landlord", contact: ContactEmail) => {
    setPendingRole(role);
    draftMutation.mutate(
      { role, contactEmail: contact.email, contactName: contact.name },
      {
        onSuccess: (res) => setResults((prev) => ({ ...prev, [role]: res })),
        onSettled: () => setPendingRole(null),
      }
    );
  };

  return (
    <div
      style={{
        marginBottom: 12,
        fontSize: 13,
        border: "1px solid #eee",
        borderRadius: 4,
        padding: 8,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Contact emails</div>
      {isLoading ? (
        <Spinner animation="border" size="sm" />
      ) : isError ? (
        <div style={{ color: "#c00" }}>Failed to load contact emails.</div>
      ) : (
        <div style={{ display: "flex", gap: 16 }}>
          <ContactColumn
            title="Tenant"
            role="tenant"
            contacts={data?.tenant ?? []}
            onDraft={onDraft}
            drafting={pendingRole === "tenant"}
            result={results.tenant}
          />
          <ContactColumn
            title="Landlord"
            role="landlord"
            contacts={data?.landlord ?? []}
            onDraft={onDraft}
            drafting={pendingRole === "landlord"}
            result={results.landlord}
          />
        </div>
      )}
      {draftMutation.isError && (
        <Alert variant="danger" style={{ marginTop: 8, marginBottom: 0 }}>
          Failed to generate the draft. Please try again.
        </Alert>
      )}
    </div>
  );
};

export default LeadContactEmailsSection;
